/**
 * Business details: the name, the clock, what the business does, where mail goes, and how
 * the dashboard looks.
 *
 * ONE CARD OF ROWS, AS SETTINGS V2 DRAWS IT, where the old Business tab was four sections
 * each with a heading and a sentence. The prototype's 220px label column does what those
 * headings did - a name and one line of help beside every control - so the four headings
 * went and the help lines stayed, shorter.
 *
 * THE SLUG IS SAID OUT LOUD UNDER THE NAME, because the obvious guess is the opposite one:
 * renaming the business does not take the agent off the air, and "Technical ID ... can't be
 * changed" is the prototype's way of saying so in one line.
 *
 * THE TIMEZONE STILL REWRITES EVERY CALL'S LOCAL HOUR when it is saved, which the save bar
 * says afterwards ("Recomputing the local time of past calls") rather than this pane saying
 * it in advance.
 */

import type { SettingsPage } from "../../../api";
import { ChoiceCard, CountTag, Mono, Select, SettingCard, SettingRow, TextArea, TextInput, cx } from "../../../ui";
import { useDraft } from "../draft";

/** The Worker's cap, as `maxLength` so the box stops rather than being refused on save. */
export const MAX_BUSINESS_CHARS = 1000;

export function DetailsPane({ agent }: { agent: NonNullable<SettingsPage["agent"]> }) {
  const { state, set } = useDraft();
  const business = state.draft.business;
  const change = (patch: Partial<typeof business>) => set("business", { ...business, ...patch });

  return (
    <SettingCard>
      <SettingRow first label="Business name" help="Shown across your dashboard." htmlFor="business-name">
        <div className="grid gap-2">
          <TextInput
            id="business-name"
            className="w-full"
            value={business.name}
            placeholder="Eco Boats Australia"
            onChange={(event) => change({ name: event.target.value })}
          />
          <span className="flex flex-wrap items-center gap-2 text-xs text-ink-faint">
            Technical ID <Mono>{agent.slug}</Mono> can't be changed.
          </span>
        </div>
      </SettingRow>

      <SettingRow label="Timezone" help="Used for opening hours and every time on the dashboard." htmlFor="business-timezone">
        <TimezonePicker id="business-timezone" value={business.timezone} onChange={(timezone) => change({ timezone })} />
      </SettingRow>

      <SettingRow label="What you do" help="A few sentences. Helps us summarise and sort your calls." htmlFor="business-description">
        <div className="grid gap-1.5">
          <TextArea
            id="business-description"
            className="w-full"
            rows={4}
            maxLength={MAX_BUSINESS_CHARS}
            value={business.description}
            placeholder="Electric outboard repowers and new electric boats. We install in Sydney and the Central Coast and ship parts Australia-wide. We do not hire boats and we do not service petrol engines."
            onChange={(event) => change({ description: event.target.value })}
          />
          <CountTag className="justify-self-end" n={business.description.length} max={MAX_BUSINESS_CHARS} />
        </div>
      </SettingRow>

      <SettingRow label="Email for calls" help="Summaries, transcripts and alerts." htmlFor="business-email">
        <TextInput
          id="business-email"
          type="email"
          className="w-full max-w-105"
          value={business.email}
          placeholder="nobody@example.com"
          onChange={(event) => change({ email: event.target.value })}
        />
      </SettingRow>

      {/* WHO READS THIS DASHBOARD, which is a fact about the business rather than about
          whoever signed in: the tablet at the counter is the shop's. The save reloads the
          current agent, which is what redraws /calls in the chosen view, and the cog in the
          top bar reaches this page from either view, so nobody can lock themselves in. */}
      <SettingRow label="Dashboard view" help="How this business looks when you sign in.">
        <div role="radiogroup" aria-label="Dashboard view" className="grid grid-cols-2 gap-2.5 max-[560px]:grid-cols-1">
          <ChoiceCard
            name="dashboard-view"
            value="default"
            checked={business.view === "default"}
            onChange={() => change({ view: "default" })}
            title="Standard"
            preview={<Sketch bars={[["h-1.5", "w-3/5", "bg-line-strong"], ["h-1", "w-[90%]", "bg-line"], ["h-1", "w-4/5", "bg-line"], ["h-1", "w-[85%]", "bg-line"]]} gap="gap-1.5" />}
          >
            Everything: charts, filters, full call list.
          </ChoiceCard>
          <ChoiceCard
            name="dashboard-view"
            value="simple"
            checked={business.view === "simple"}
            onChange={() => change({ view: "simple" })}
            title="Simplified"
            preview={<Sketch bars={[["h-3", "w-[70%]", "bg-ink"], ["h-2.5", "w-1/2", "bg-line-strong"]]} gap="gap-2" />}
          >
            Big text for a tablet at the front desk. Today's calls only.
          </ChoiceCard>
        </div>
      </SettingRow>
    </SettingCard>
  );
}

/** A thumbnail of each view, drawn from bars rather than a screenshot that would go stale. */
function Sketch({ bars, gap }: { bars: Array<[string, string, string]>; gap: string }) {
  return (
    <span aria-hidden="true" className={cx("flex h-16 flex-col rounded-sm bg-sunken p-2.5", gap)}>
      {bars.map(([h, w, c], index) => (
        <span key={index} className={cx("rounded-[3px]", h, w, c)} />
      ))}
    </span>
  );
}

/**
 * Every timezone this browser knows, or a text box.
 *
 * `Intl.supportedValuesOf` is the whole IANA database and needs no list in our source: a zone
 * added by a tzdata update appears without a deploy. It is missing on older browsers, so the
 * fallback is a plain input - the Worker validates whatever arrives and answers with a
 * sentence. "Not set" is a real option: NULL means "we do not know where this business is",
 * and every chart that groups by local time says so, which beats a confident wrong clock.
 */
export function TimezonePicker({ id, value, onChange }: { id?: string; value: string; onChange: (next: string) => void }) {
  const zones = supportedTimezones();
  if (!zones) {
    return <TextInput id={id} className="w-full max-w-90" value={value} placeholder="Australia/Sydney" onChange={(e) => onChange(e.target.value)} />;
  }
  return (
    <Select id={id} className="w-full max-w-90" value={value} onChange={(e) => onChange(e.target.value)}>
      <option value="">Not set</option>
      {zones.map((zone) => (
        <option key={zone} value={zone}>
          {zone.replace(/_/g, " ")}
        </option>
      ))}
    </Select>
  );
}

/** Computed once. The list is ~450 strings and does not change while the tab is open. */
let cachedZones: string[] | null | undefined;

function supportedTimezones(): string[] | null {
  if (cachedZones === undefined) {
    const supported = (Intl as { supportedValuesOf?: (key: string) => string[] }).supportedValuesOf;
    cachedZones = supported ? supported("timeZone") : null;
  }
  return cachedZones;
}
